// ============================================================================
// CHANGO V5.0 — Ops Audit Queue Page (§9.6.2d 阶段 3：管理员复核)
// ============================================================================
// 复核队列页 /ops/workspaces/audit-queue
// 默认列出 audit_flag in (pending_audit, resubmitted) 的 workspace
// 表格列：公司名 / 负责人 / 套餐 / 营业执照 / 提交时间 / 试用到期 / [✓ 通过] / [✗ 不符冻结]
// 点 [✗] 弹 modal 必填理由文本框，提交 POST /api/ops/workspaces/:id/audit-mismatch
// 点 [✓] 直接 POST /api/ops/workspaces/:id/audit-pass
// 操作完成自动从队列消失，刷新列表
// ============================================================================

import React, { useState, useEffect } from 'react';
import { api, HttpError } from '../../api/client.js';
import { brandForSku, SERIES_COLORS } from '../../lib/skuBrandVersion.js';

export default function OpsAuditQueue() {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalReason, setModalReason] = useState('');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);
  const [processError, setProcessError] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchQueue();
  }, []);

  const fetchQueue = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.get('/api/ops/workspaces/audit-queue?status=pending_audit,resubmitted');
      setWorkspaces(data?.items || []);
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : '加载队列失败';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handlePass = async (workspaceId) => {
    if (!window.confirm('确认通过审核？')) return;
    setProcessing(true);
    setProcessError(null);
    try {
      await api.post(`/api/ops/workspaces/${workspaceId}/audit-pass`);
      setWorkspaces(ws => ws.filter(w => w.id !== workspaceId));
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : '操作失败';
      setProcessError(msg);
    } finally {
      setProcessing(false);
    }
  };

  const handleMismatchClick = (workspaceId) => {
    setSelectedWorkspaceId(workspaceId);
    setModalReason('');
    setProcessError(null);
    setModalOpen(true);
  };

  const handleSubmitMismatch = async () => {
    if (!modalReason.trim() || modalReason.length < 10) {
      setProcessError('理由至少 10 个字符');
      return;
    }

    setProcessing(true);
    setProcessError(null);
    try {
      await api.post(`/api/ops/workspaces/${selectedWorkspaceId}/audit-mismatch`, {
        reason: modalReason
      });
      setWorkspaces(ws => ws.filter(w => w.id !== selectedWorkspaceId));
      setModalOpen(false);
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : '操作失败';
      setProcessError(msg);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-slate-500">
          <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-chango-500 border-t-transparent" aria-hidden="true" />
          <span className="text-sm">加载复核队列中…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-800">试用工作区审核</h1>
        <p className="text-slate-600 mt-2">
          审核新注册用户的营业执照和企业信息真实性
        </p>
      </div>

      {error ? (
        <div role="alert" className="alert-error mb-6">{error}</div>
      ) : null}

      {processError ? (
        <div role="alert" className="alert-error mb-6">{processError}</div>
      ) : null}

      {workspaces.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center">
          <p className="text-slate-600">暂无待审核的工作区</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 shadow">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">公司名</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">负责人</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">套餐</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">营业执照</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">提交时间</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">试用到期</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {workspaces.map((ws) => {
                const workspaceId = ws.id ?? ws.workspace_id;
                const brand = brandForSku(ws.sku_code);
                const colors = SERIES_COLORS[brand.series] || SERIES_COLORS._;
                const submittedAtRaw = ws.audit_submitted_at ?? ws.submitted_at;
                const submittedAt = submittedAtRaw ? new Date(submittedAtRaw).toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
                const expiresAt = ws.trial_expires_at ? new Date(ws.trial_expires_at).toLocaleDateString('zh-CN') : '—';

                return (
                  <tr key={workspaceId} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{ws.company_name}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      <div className="font-medium text-slate-800">{ws.owner_display_name || ws.owner_name || ws.owner_email || '—'}</div>
                      <div className="text-xs text-slate-500">{ws.owner_email || `用户 #${ws.owner_user_id || '—'}`}</div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        <span className={`inline-block h-2 w-2 rounded-full ${colors.dot}`} />
                        <span className="font-medium text-slate-700">{brand.displayCode || 'N/A'}</span>
                        <span className="text-slate-600">{brand.shortName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {ws.license_file_url ? (
                        <a
                          href={ws.license_file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-chango-600 hover:underline"
                        >
                          查看执照
                        </a>
                      ) : (
                        <span className="text-slate-500">未上传</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{submittedAt}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{expiresAt}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => handlePass(workspaceId)}
                          disabled={processing}
                          className="inline-flex items-center gap-1 rounded bg-green-100 px-3 py-1 text-sm font-medium text-green-700 hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ✓ 通过
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMismatchClick(workspaceId)}
                          disabled={processing}
                          className="inline-flex items-center gap-1 rounded bg-red-100 px-3 py-1 text-sm font-medium text-red-700 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ✗ 冻结
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 冻结理由 Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-lg font-semibold text-slate-800">
              冻结并要求重新提交资料
            </h2>
            <p className="mb-4 text-sm text-slate-600">
              请输入冻结理由（至少 10 个字符），该理由将通知给用户。
            </p>
            <textarea
              value={modalReason}
              onChange={(e) => setModalReason(e.target.value)}
              disabled={processing}
              placeholder="输入管理员理由…"
              className={`w-full h-32 p-3 border rounded-lg text-sm ${modalReason.length >= 10 ? 'border-green-300 bg-green-50' : 'border-slate-300'} disabled:opacity-50`}
            />
            <p className={`text-xs mt-2 ${modalReason.length >= 10 ? 'text-green-600' : 'text-slate-500'}`}>
              {modalReason.length}/10 字符
            </p>
            {processError ? (
              <p className="alert-error mt-3">{processError}</p>
            ) : null}
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                disabled={processing}
                className="btn btn-outline"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleSubmitMismatch}
                disabled={!modalReason.trim() || modalReason.length < 10 || processing}
                className="btn btn-danger disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? '提交中…' : '确认冻结'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
